/**
 * Generic resource query factory
 * Creates standardized query hooks for any resource type
 */

import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/store/organization.store';
import type {
  BaseFilters,
  PaginatedResponse,
  BaseQueryOptions,
  DetailQueryOptions,
  ResourceQueryConfig,
} from '@/types/resource.types';
import type { QueryKeyFactory, ListQueryResult, DetailQueryResult, CountQueryResult } from '@/types/query.types';

/**
 * Default cache configuration
 */
const DEFAULT_CACHE_CONFIG = {
  listStaleTime: 60 * 1000, // 1 minute
  listGcTime: 10 * 60 * 1000, // 10 minutes
  detailStaleTime: 60 * 1000, // 1 minute
  detailGcTime: 10 * 60 * 1000, // 10 minutes
  countStaleTime: 30 * 1000, // 30 seconds
  countGcTime: 5 * 60 * 1000, // 5 minutes
};

/**
 * Creates a list query hook for a resource
 */
export function createListQuery<T, TFilters extends BaseFilters = BaseFilters>(
  config: ResourceQueryConfig<T, TFilters>,
  queryKeyFactory: QueryKeyFactory
) {
  const cacheConfig = { ...DEFAULT_CACHE_CONFIG, ...config.cacheConfig };
  const requiresOrg = config.requiresOrganization !== false; // default true

  return function useListQuery(
    filters?: TFilters,
    options?: BaseQueryOptions
  ): ListQueryResult<T> {
    const { currentOrganization } = useOrganizationStore();
    const enabled = (options?.enabled !== false) && (!requiresOrg || !!currentOrganization?.id);

    return useQuery<PaginatedResponse<T>, Error>({
      queryKey: queryKeyFactory.list(filters as Record<string, unknown>),
      queryFn: async () => {
        if (requiresOrg && !currentOrganization?.id) {
          throw new Error('No organization selected');
        }
        return config.api.getList(filters);
      },
      enabled,
      placeholderData: (previous) => previous,
      staleTime: options?.staleTime ?? cacheConfig.listStaleTime,
      gcTime: options?.gcTime ?? cacheConfig.listGcTime,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      refetchOnReconnect: options?.refetchOnReconnect ?? true,
      refetchOnMount: options?.refetchOnMount ?? true, // Changed to true for initial fetch
    });
  };
}

/**
 * Creates a detail query hook for a resource
 */
export function createDetailQuery<T>(
  config: ResourceQueryConfig<T>,
  queryKeyFactory: QueryKeyFactory
) {
  const cacheConfig = { ...DEFAULT_CACHE_CONFIG, ...config.cacheConfig };
  const requiresOrg = config.requiresOrganization !== false;

  return function useDetailQuery(
    id: string | undefined,
    options?: DetailQueryOptions
  ): DetailQueryResult<T> {
    const { currentOrganization } = useOrganizationStore();

    return useQuery<T, Error>({
      queryKey: queryKeyFactory.detail(id || '', {
        includeCustomFields: options?.includeCustomFields !== false,
      }),
      queryFn: () => config.api.getDetail(id!, options),
      enabled: (options?.enabled !== false) && !!id && (!requiresOrg || !!currentOrganization?.id),
      staleTime: options?.staleTime ?? cacheConfig.detailStaleTime,
      gcTime: options?.gcTime ?? cacheConfig.detailGcTime,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      refetchOnReconnect: options?.refetchOnReconnect ?? true,
      refetchOnMount: options?.refetchOnMount ?? true, // Changed to true for initial fetch
    });
  };
}

/**
 * Creates a count query hook for a resource
 */
export function createCountQuery<T>(
  config: ResourceQueryConfig<T>,
  queryKeyFactory: QueryKeyFactory
) {
  const cacheConfig = { ...DEFAULT_CACHE_CONFIG, ...config.cacheConfig };
  const requiresOrg = config.requiresOrganization !== false;

  return function useCountQuery(options?: BaseQueryOptions): CountQueryResult {
    const { currentOrganization } = useOrganizationStore();

    if (!config.api.getCount) {
      throw new Error('getCount API method not provided in configuration');
    }

    return useQuery<{ count: number }, Error>({
      queryKey: [...queryKeyFactory.all, 'count'],
      queryFn: config.api.getCount,
      enabled: (options?.enabled !== false) && (!requiresOrg || !!currentOrganization?.id),
      staleTime: options?.staleTime ?? cacheConfig.countStaleTime,
      gcTime: options?.gcTime ?? cacheConfig.countGcTime,
      refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
      refetchOnMount: options?.refetchOnMount ?? true, // Changed to true for initial fetch
    });
  };
}

/**
 * Creates a complete set of query hooks for a resource
 */
export function createResourceQueries<T, TFilters extends BaseFilters = BaseFilters>(
  config: ResourceQueryConfig<T, TFilters>,
  queryKeyFactory: QueryKeyFactory
) {
  return {
    useListQuery: createListQuery(config, queryKeyFactory),
    useDetailQuery: createDetailQuery(config, queryKeyFactory),
    ...(config.api.getCount ? { useCountQuery: createCountQuery(config, queryKeyFactory) } : {}),
  };
}
